import { createContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from 'src/components/common/typography/Typography';
import Tabs, { TTabItem } from 'src/components/common/tabs/Tabs';
import { useAppSelector, useAppDispatch } from 'src/store/hooks';
import {
  About,
  Activities,
  AppliedProgramsList,
  Education,
  ProfileCard,
  WorkExperience
} from 'src/components/profile';
import Header from 'src/components/header/Header';
import { StorageUtils } from 'src/utils';
import { getProfile } from 'src/store/profile/profile.slice';
import { TGetUserStatsRequest } from 'src/services/apiEndpoint.types.ts';
import { getUserStats, incrementNewsFeedPage } from 'src/store/socialMedia/socialMedia.slice';
import { useGetIdentity } from 'src/hooks';
import { Button, ConditionalComponent, InfiniteScroll, Loader } from 'src/components/common';
import {
  CompanyCard,
  CreateCompanyCard,
  InviteNetwork,
  StartupNewsResources
} from 'src/components/cards';

import './myProfile.scss';
import ViewCohort from 'src/components/profile/Cohorts/ViewCohort/ViewCohort';
import CreateCompanyNowCard from 'src/components/CreateCompanyNowCard/CreateCompanyNowCard';
export type TMyProfileTabs =
  | 'about'
  | 'work-experience'
  | 'education'
  | 'my-activities'
  | 'applied-programs';

export const SelfUserContext = createContext(false);

const MyProfile = () => {
  const [activeProfileTab, setActiveProfileTab] = useState<TMyProfileTabs>('about');

  const { profile, profileLoading } = useAppSelector((store) => store.profile);
  const { newsFeedMoreItemsLeft, socialLoading } = useAppSelector((store) => store.socialMedia);

  // Hooks
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { getIdentities } = useGetIdentity();

  const { loggedInUserId, loggedInUserType, loggedInCompanyId } = getIdentities();

  const loadMoreActivities = () => {
    if (newsFeedMoreItemsLeft && !socialLoading && activeProfileTab === 'my-activities') {
      dispatch(incrementNewsFeedPage(1));
    }
  };

  const {
    about,
    address,
    dob,
    email,
    gender,
    introduction,
    linkedin,
    facebook,
    twitter,
    instagram,
    name,
    phoneNumber,
    profilePic,
    role,
    userExpertise,
    userIndustries,
    country,
    state,
    city,
    zipCode
  } = profile;

  const tabItems: TTabItem[] = [
    {
      key: 'about',
      text: 'About',
      disabled: false,
      component: (
        <About
          about={about}
          address={address}
          dob={dob}
          email={email}
          gender={gender}
          linkedin={linkedin}
          facebook={facebook}
          twitter={twitter}
          instagram={instagram}
          phoneNumber={phoneNumber}
          role={role}
          userExpertise={userExpertise}
          userIndustries={userIndustries}
          country={country}
          state={state}
          city={city}
          zipCode={zipCode}
          isSelf={true}
        />
      )
    },
    {
      key: 'work-experience',
      text: 'Work Experience',
      disabled: false,
      component: <WorkExperience experiences={profile?.experience || []} />
    },
    {
      key: 'education',
      text: 'Education',
      disabled: false,
      component: <Education education={profile?.education || []} />
    },
    {
      key: 'my-activities',
      text: 'My Activities',
      disabled: false,
      component: (
        <Activities userId={profile?.id} viewerId={loggedInUserId} viewerType={loggedInUserType} />
      )
    },
    {
      key: 'applied-programs',
      text: 'Applied Programs',
      disabled: false,
      component: <AppliedProgramsList />
    }
    // {
    //   key: 'cohorts',
    //   text: 'Cohorts',
    //   disabled: false,
    //   component: <ViewCohort />
    // }
  ];

  const changeMyProfileTab = (value: string) => {
    setActiveProfileTab(value as TMyProfileTabs);
  };

  const handleGetUserStats = ({ id }: { id: string }) => {
    const payload: TGetUserStatsRequest = {
      userId: id,
      userType: 'User'
    };

    dispatch(getUserStats(payload));
  };

  useEffect(() => {
    const isProfileCompany = StorageUtils.get('userType') === 'Company';
    // If it is not a company and
    // If the profile exists and
    // Role doesn't exist, redirect the user to onboarding screen
    if (profile.id && !profile.role && !isProfileCompany) {
      navigate('/onboarding-user-type');
    }
  }, [profile.id]);

  useEffect(() => {
    const id = StorageUtils.get('userId') as unknown as string;
    handleGetUserStats({ id });
    dispatch(getProfile({ id }));
  }, []);

  return (
    <SelfUserContext.Provider value={true}>
      <InfiniteScroll
        className="my-profile"
        onScrollEnd={loadMoreActivities}
        loading={socialLoading}
        moreItemsLeft={(activeProfileTab === 'applied-programs' || activeProfileTab === 'my-activities') ? false : newsFeedMoreItemsLeft}
        showFooter={false}
      >
        <Header />
        <div className="my-profile__body">
          <Typography as="p" variant="subHeading-1" fontWeight="semiBold">
            My Profile
          </Typography>
          {profileLoading ? <Loader /> : <div className="my-profile__body__content">
            <div className="my-profile__body__content__body">
              <ProfileCard
                introduction={introduction}
                name={name}
                profilePic={profilePic}
                role={role}
                userId={profile.id}
              />
              <Tabs
                activeItem={activeProfileTab}
                onTabChange={changeMyProfileTab}
                items={tabItems}
              />
              {activeProfileTab === 'my-activities' && <>
                {socialLoading && <Loader />}
                {!socialLoading && newsFeedMoreItemsLeft && (
                  <Button
                    className="infinite-scroll-component__footer"
                    variant="tertiary"
                    size="extraSmall"
                    onClick={loadMoreActivities}
                  >
                    {newsFeedMoreItemsLeft ? 'Load more items' : <>&bull;</>}
                  </Button>
                )}
              </>}
            </div>
            <div className="my-profile__body__content__cards">
              <InviteNetwork />
              {loggedInUserType === 'User' && (
                <ConditionalComponent
                  condition={Boolean(loggedInCompanyId)}
                  whenTrue={<CompanyCard selfCompany={true} companyId={loggedInCompanyId} />}
                  whenFalse={<CreateCompanyNowCard />}
                />
              )}
              {/* <StartupNewsResources /> */}
            </div>
          </div>}
        </div>
      </InfiniteScroll>
    </SelfUserContext.Provider>
  );
};

export default MyProfile;
